import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { StringParam, useQueryParam } from "use-query-params";

import { useUnreadMessagesWebsocketContext } from "common/context/UnreadMessagesWebsocketContext";
import useUnreadMessagesCount from "common/datahooks/useUnreadMessagesCount";
import type { Lead } from "common/types";

import useLead from "../../leads/datahooks/useLead";
import useLeadConversationUrn from "../datahooks/useLeadConversationUrn";

import type { Conversation } from "../types";

export default function useActiveConversation(
  conversations: Conversation[] | undefined,
  onUnreadMessage: () => void,
) {
  // TODO: Refactor how lead is handled
  const { state }: { state: { lead?: Lead; selectedAccountId?: string } } =
    useLocation();

  const [activeConversationUrn, setActiveConversationUrn] = useQueryParam(
    "activeConversation",
    StringParam,
  );
  const [activeAccountId, setActiveAccountId] = useQueryParam(
    "activeAccount",
    StringParam,
  );
  const [isStateLeadEnabled, setIsStateLeadEnabled] = useState(true);

  const { resetUnreadMessages } = useUnreadMessagesCount();

  useUnreadMessagesWebsocketContext(({ unreadCount, shouldRefetch }) => {
    if (unreadCount > 0) {
      resetUnreadMessages();
    }
    if (shouldRefetch) {
      onUnreadMessage();
    }
  });

  const leadConversationUrn = useLeadConversationUrn({
    leadId: state?.lead?.id,
    accountId: state?.lead?.lead_of[0],
  });

  const { lead } = useLead({ profileId: state?.lead?.id });

  // Update when labels are updated
  const activeConversation = useMemo(() => {
    if (conversations && activeConversationUrn) {
      return conversations.find(
        ({ conversation_urn, owner: accountId }) =>
          conversation_urn === activeConversationUrn &&
          accountId === activeAccountId,
      );
    }

    if (isStateLeadEnabled && state?.lead) {
      return {
        conversation_urn: leadConversationUrn,
        participants: [lead || { ...state.lead, is_lead: true }],
        unread_count: 0,
        last_activity_at: Date.now(),
        owner: state.selectedAccountId || state.lead.lead_of[0],
        connected: true,
      };
    }
  }, [
    lead,
    leadConversationUrn,
    activeConversationUrn,
    activeAccountId,
    isStateLeadEnabled,
    state,
    conversations,
  ]);

  const setActiveConversation = useCallback(
    (urn: string, accountId: string) => {
      setActiveConversationUrn(urn);
      setActiveAccountId(accountId);
      setIsStateLeadEnabled(false);
    },
    [],
  );

  return { activeConversation, setActiveConversation };
}
